import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import LoadingSpinner from '../common/LoadingSpinner';

const LoginForm = () => {
  const { login } = useAuth();
  const { showError, showSuccess } = useApp();
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email no es válido';
    }
    
    if (!formData.password) {
      newErrors.password = 'Contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Contraseña debe tener al menos 6 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        showSuccess('Inicio de sesión exitoso', 'Bienvenido');
      } else {
        showError(result.message, 'Error de autenticación');
      }
    } catch (error) {
      showError('Error de conexión. Intenta nuevamente.', 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fillDemoCredentials = (role) => {
    const credentials = {
      administrador: { email: 'admin@educapp.cl', password: 'admin123' },
      gestor: { email: 'gestor@educapp.cl', password: 'gestor123' },
      profesor: { email: 'profesor@educapp.cl', password: 'prof123' },
      alumno: { email: 'alumno@educapp.cl', password: 'alum123' },
      apoderado: { email: 'apoderado@educapp.cl', password: 'apod123' }
    };

    const selectedCredentials = credentials[role];
    if (selectedCredentials) {
      setFormData(selectedCredentials);
      setErrors({});
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-100 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            ABP360
          </h2>
          <p className="text-lg text-gray-600 mb-2">
            Sistema de Gestión Escolar ABP
          </p>
          <p className="text-sm text-gray-500">
            Ingresa tus credenciales para acceder al sistema
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`
                    appearance-none relative block w-full pl-10 pr-3 py-3 
                    border ${errors.email ? 'border-red-300' : 'border-gray-300'} 
                    placeholder-gray-500 text-gray-900 rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                    focus:z-10 sm:text-sm transition-colors duration-200
                  `}
                  placeholder="tu@educapp.cl"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`
                    appearance-none relative block w-full pl-10 pr-3 py-3 
                    border ${errors.password ? 'border-red-300' : 'border-gray-300'} 
                    placeholder-gray-500 text-gray-900 rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                    focus:z-10 sm:text-sm transition-colors duration-200
                  `}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`
                  group relative w-full flex justify-center py-3 px-4 border border-transparent 
                  text-sm font-medium rounded-lg text-white 
                  ${isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  } 
                  transition-colors duration-200
                `}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <LoadingSpinner size="sm" color="white" showMessage={false} />
                    <span className="ml-2">Iniciando sesión...</span>
                  </div>
                ) : (
                  <>
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-primary-500 group-hover:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                    </span>
                    Iniciar Sesión
                  </>
                )}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-500 transition-colors duration-200"
                onClick={() => alert('Funcionalidad en desarrollo')}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>

          {/* Demo Credentials Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowDemoCredentials(!showDemoCredentials)}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 flex items-center justify-center"
            >
              <svg className={`w-4 h-4 mr-2 transform transition-transform duration-200 ${showDemoCredentials ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Credenciales de prueba
            </button>

            {showDemoCredentials && (
              <div className="mt-4 space-y-2 animate-fade-in">
                <p className="text-xs text-gray-500 text-center mb-3">
                  Haz clic en cualquier rol para llenar automáticamente los campos
                </p>
                
                {[
                  { role: 'administrador', label: 'Administrador', color: 'purple', email: 'admin@educapp.cl' },
                  { role: 'gestor', label: 'Gestor', color: 'blue', email: 'gestor@educapp.cl' },
                  { role: 'profesor', label: 'Profesor', color: 'green', email: 'profesor@educapp.cl' },
                  { role: 'alumno', label: 'Alumno', color: 'yellow', email: 'alumno@educapp.cl' },
                  { role: 'apoderado', label: 'Apoderado', color: 'orange', email: 'apoderado@educapp.cl' }
                ].map(({ role, label, color, email }) => (
                  <button
                    key={role}
                    onClick={() => fillDemoCredentials(role)}
                    className={`
                      w-full text-left px-3 py-2 text-xs rounded-lg transition-colors duration-200
                      ${color === 'purple' ? 'bg-purple-50 hover:bg-purple-100 text-purple-800' : ''}
                      ${color === 'blue' ? 'bg-blue-50 hover:bg-blue-100 text-blue-800' : ''}
                      ${color === 'green' ? 'bg-green-50 hover:bg-green-100 text-green-800' : ''}
                      ${color === 'yellow' ? 'bg-yellow-50 hover:bg-yellow-100 text-yellow-800' : ''}
                      ${color === 'orange' ? 'bg-orange-50 hover:bg-orange-100 text-orange-800' : ''}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{label}</span>
                      <span className="text-gray-500">{email}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 ABP360. Sistema de Gestión Escolar ABP
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;